/**
 * OTF/RTF Report Generation & Distribution
 * Generates Outreach Tracking Forms (OTF) and Referral Tracking Forms (RTF)
 * for CalAIM claim submissions.
 */

import { useTabbedChrome } from '@/components/weavein';
import { useState, useEffect, useCallback } from 'react';
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileText, Download, Send, Calendar, Filter, RefreshCw, History, BarChart3 } from "lucide-react";
import { getWeaveinApiUrl } from '@/src/config';

interface OTFRTFSubmissionProps {
  onSubmit?: (submission: any) => void;
  onBack?: () => void;
}

type ReportType = 'otf' | 'rtf';
type TabType = 'generate' | 'history';

interface ReportSummary {
  total_outreach_attempts?: number;
  successful_contacts?: number;
  no_response?: number;
  contact_rate?: number;
  total_referrals?: number;
  completed_referrals?: number;
  completion_rate?: number;
  total_service_units?: number;
  total_claim_amount?: number;
}

interface ReportRecord {
  member_name: string;
  cin?: string;
  [key: string]: any;
}

interface ReportData {
  report_type: string;
  date_range: { start: string; end: string };
  summary: ReportSummary;
  records: ReportRecord[];
}

interface HistoryItem {
  id: number;
  report_type: string;
  export_format: string;
  date_range_start?: string;
  date_range_end?: string;
  distributed_to?: string[];
  distributed_at?: string;
  created_at: string;
}

interface AffiliationOption {
  id: number;
  payer_name: string | null;
  program_name: string | null;
  health_plan_name: string | null;
}

export function OTFRTFSubmission({ onSubmit, onBack }: OTFRTFSubmissionProps) {
  const tabbed = useTabbedChrome();
  const [reportType, setReportType] = useState<ReportType>('otf');
  const [activeTab, setActiveTab] = useState<TabType>('generate');
  const [dateStart, setDateStart] = useState(() => {
    const d = new Date(); d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [dateEnd, setDateEnd] = useState(() => new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  // MCO format selector
  const [affiliations, setAffiliations] = useState<AffiliationOption[]>([]);
  const [selectedAffiliationId, setSelectedAffiliationId] = useState<string>('');

  const apiUrl = getWeaveinApiUrl();

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id');
    return {
      'Authorization': `Bearer ${token}`,
      'X-CBO-ID': cboId || '',
      'Content-Type': 'application/json',
    };
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const endpoint = reportType === 'otf' ? 'otf' : 'rtf';
      const res = await fetch(
        `${apiUrl}/api/reports/${endpoint}?date_start=${dateStart}&date_end=${dateEnd}`,
        { headers: getHeaders() }
      );
      if (!res.ok) throw new Error(`Failed to load report: ${res.status}`);
      const data = await res.json();
      setReportData(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = async () => {
    try {
      const endpoint = reportType === 'otf' ? 'otf' : 'rtf';
      const body: Record<string, any> = {
        date_range_start: dateStart,
        date_range_end: dateEnd,
        format: 'csv',
      };
      if (selectedAffiliationId) {
        body.affiliation_id = parseInt(selectedAffiliationId, 10);
      }
      const res = await fetch(`${apiUrl}/api/reports/${endpoint}/generate`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType.toUpperCase()}_Report_${dateStart}_${dateEnd}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const loadHistory = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/api/reports/otf-rtf/history`, { headers: getHeaders() });
      if (res.ok) setHistory(await res.json());
    } catch {}
  }, [apiUrl, getHeaders]);

  useEffect(() => {
    if (activeTab === 'history') loadHistory();
  }, [activeTab, loadHistory]);

  useEffect(() => {
    const fetchAffiliations = async () => {
      try {
        const cboId = localStorage.getItem('active_cbo_id') || '1';
        const res = await fetch(`${apiUrl}/api/organization-setup/${cboId}/affiliations`, { headers: getHeaders() });
        if (res.ok) setAffiliations(await res.json());
      } catch {}
    };
    fetchAffiliations();
  }, [apiUrl, getHeaders]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {onBack && (
            <Button variant="outline" size="sm" onClick={onBack}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          )}
          <div>
            {!tabbed && (
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="w-6 h-6 text-blue-600" />
              OTF / RTF Reports
            </h1>
            )}
            <p className="text-sm text-gray-500 mt-1">
              Generate and distribute Outreach Tracking and Referral Tracking forms for CalAIM claims
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('generate')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition ${
            activeTab === 'generate' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <BarChart3 className="w-4 h-4 inline mr-1" /> Generate Reports
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition ${
            activeTab === 'history' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <History className="w-4 h-4 inline mr-1" /> Report History
        </button>
      </div>

      {activeTab === 'generate' && (
        <>
          {/* Controls */}
          <div className="bg-white rounded-xl border p-4 flex flex-wrap items-end gap-4">
            {/* Report Type */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Report Type</label>
              <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
                <button
                  onClick={() => { setReportType('otf'); setReportData(null); }}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                    reportType === 'otf' ? 'bg-blue-600 text-white shadow' : 'text-gray-600 hover:bg-gray-200'
                  }`}
                >OTF</button>
                <button
                  onClick={() => { setReportType('rtf'); setReportData(null); }}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                    reportType === 'rtf' ? 'bg-blue-600 text-white shadow' : 'text-gray-600 hover:bg-gray-200'
                  }`}
                >RTF</button>
              </div>
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                <Calendar className="w-3 h-3 inline mr-1" />Start Date
              </label>
              <input type="date" value={dateStart} onChange={e => setDateStart(e.target.value)}
                className="border rounded-lg px-3 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">End Date</label>
              <input type="date" value={dateEnd} onChange={e => setDateEnd(e.target.value)}
                className="border rounded-lg px-3 py-1.5 text-sm" />
            </div>

            {/* MCO Format (optional) */}
            {affiliations.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  <Filter className="w-3 h-3 inline mr-1" />MCO Format (optional)
                </label>
                <select
                  value={selectedAffiliationId}
                  onChange={e => setSelectedAffiliationId(e.target.value)}
                  className="border rounded-lg px-3 py-1.5 text-sm bg-white min-w-[200px]"
                >
                  <option value="">Default format</option>
                  {affiliations.map(aff => (
                    <option key={aff.id} value={String(aff.id)}>
                      {aff.payer_name || aff.health_plan_name || `Affiliation #${aff.id}`}
                      {aff.program_name ? ` — ${aff.program_name}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Actions */}
            <Button onClick={fetchReport} disabled={loading} className="bg-v2accent hover:bg-v2accent/90 text-white">
              {loading ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Filter className="w-4 h-4 mr-1" />}
              Generate
            </Button>
            {reportData && (
              <Button variant="outline" onClick={exportCSV}>
                <Download className="w-4 h-4 mr-1" /> Export CSV
              </Button>
            )}
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}

          {/* Summary Cards */}
          {reportData && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {reportType === 'otf' ? (
                <>
                  <SummaryCard label="Total Outreach" value={reportData.summary.total_outreach_attempts || 0} />
                  <SummaryCard label="Successful Contacts" value={reportData.summary.successful_contacts || 0} color="green" />
                  <SummaryCard label="No Response" value={reportData.summary.no_response || 0} color="amber" />
                  <SummaryCard label="Contact Rate" value={`${reportData.summary.contact_rate || 0}%`} color="blue" />
                </>
              ) : (
                <>
                  <SummaryCard label="Total Referrals" value={reportData.summary.total_referrals || 0} />
                  <SummaryCard label="Completed" value={reportData.summary.completed_referrals || 0} color="green" />
                  <SummaryCard label="Service Units" value={reportData.summary.total_service_units || 0} color="blue" />
                  <SummaryCard label="Claim Amount" value={`$${(reportData.summary.total_claim_amount || 0).toLocaleString()}`} color="emerald" />
                </>
              )}
            </div>
          )}

          {/* Data Table */}
          {reportData && reportData.records.length > 0 && (
            <div className="bg-white rounded-xl border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      {reportType === 'otf' ? (
                        <>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">CIN</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Health Plan</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        </>
                      ) : (
                        <>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">CIN</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Direction</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">CPT</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {reportData.records.map((r, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        {reportType === 'otf' ? (
                          <>
                            <td className="px-4 py-3 font-medium">{r.member_name}</td>
                            <td className="px-4 py-3 text-gray-500">{r.cin || '-'}</td>
                            <td className="px-4 py-3 text-gray-500">{r.health_plan || '-'}</td>
                            <td className="px-4 py-3">{r.outreach_date}</td>
                            <td className="px-4 py-3 capitalize">{r.outreach_method}</td>
                            <td className="px-4 py-3">
                              <StatusBadge status={r.response_status} />
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-4 py-3 font-medium">{r.member_name}</td>
                            <td className="px-4 py-3 text-gray-500">{r.cin || '-'}</td>
                            <td className="px-4 py-3">{r.referral_date}</td>
                            <td className="px-4 py-3 capitalize">{r.direction}</td>
                            <td className="px-4 py-3">{r.referral_reason}</td>
                            <td className="px-4 py-3">
                              <StatusBadge status={r.referral_status} />
                            </td>
                            <td className="px-4 py-3 font-mono text-xs">{r.cpt_code || '-'}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="border-t px-4 py-3 text-xs text-gray-500">
                Showing {reportData.records.length} records for {reportData.date_range.start} to {reportData.date_range.end}
              </div>
            </div>
          )}

          {reportData && reportData.records.length === 0 && (
            <div className="bg-white rounded-xl border p-12 text-center text-gray-500">
              No records found for the selected date range.
            </div>
          )}
        </>
      )}

      {activeTab === 'history' && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
            <h3 className="font-medium text-gray-900">Previously Generated Reports</h3>
            <Button variant="outline" size="sm" onClick={loadHistory}>
              <RefreshCw className="w-3 h-3 mr-1" /> Refresh
            </Button>
          </div>
          {history.length === 0 ? (
            <div className="p-12 text-center text-gray-500">No reports generated yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Format</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Range</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Generated</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Distributed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {history.map(h => (
                  <tr key={h.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium uppercase">{h.report_type}</td>
                    <td className="px-4 py-3 uppercase text-gray-500">{h.export_format || 'json'}</td>
                    <td className="px-4 py-3">{h.date_range_start} to {h.date_range_end}</td>
                    <td className="px-4 py-3 text-gray-500">{new Date(h.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      {h.distributed_at ? (
                        <span className="inline-flex items-center gap-1 text-green-700 bg-green-50 px-2 py-0.5 rounded-full text-xs">
                          <Send className="w-3 h-3" /> Sent
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">Not distributed</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, color = 'gray' }: { label: string; value: string | number; color?: string }) {
  const colorMap: Record<string, string> = {
    gray: 'bg-gray-50 text-gray-900',
    green: 'bg-green-50 text-green-900',
    amber: 'bg-amber-50 text-amber-900',
    blue: 'bg-blue-50 text-blue-900',
    emerald: 'bg-emerald-50 text-emerald-900',
  };
  return (
    <div className={`rounded-xl p-4 ${colorMap[color] || colorMap.gray}`}>
      <div className="text-xs font-medium opacity-70 mb-1">{label}</div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const s = (status || '').toLowerCase();
  const styles: Record<string, string> = {
    enrolled: 'bg-green-100 text-green-800',
    interested: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    accepted: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-yellow-100 text-yellow-800',
    pending: 'bg-gray-100 text-gray-800',
    no_response: 'bg-red-100 text-red-800',
    declined: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${styles[s] || 'bg-gray-100 text-gray-600'}`}>
      {status?.replace(/_/g, ' ') || 'Unknown'}
    </span>
  );
}
